import React, { useState } from "react";
import { AuthProviderButtons } from "../../../components/auth/AuthProviderButtons";
import AuthenticityTokenField from "../../../components/forms/AuthenticityTokenField";
import { Button } from "../../../components/ui/Buttons";
import { FieldLabel, TextInput } from "../../../components/ui/FormControls";
import { SurfaceCard } from "../../../components/ui/SurfaceCard";
import { AppLink } from "../../../components/ui/link";
import { AuthPageLayout } from "./AuthPageLayout";
import type { MainAppPageProps } from "../types";

type SignupPageProps = Pick<
  MainAppPageProps,
  "csrfToken" | "routes" | "oauthProviders" | "userForm"
>;

export const SignupPage: React.FC<SignupPageProps> = ({
  csrfToken,
  routes,
  oauthProviders = { google: false, apple: false },
  userForm,
}) => {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const hasOAuth = oauthProviders.google || oauthProviders.apple;
  const showForm = showEmailForm || !hasOAuth;

  return (
    <AuthPageLayout>
      <h1 className="mb-3 text-center font-display text-3xl font-semibold tracking-tight text-foreground">
        Create your account
      </h1>
      <p className="mb-8 text-center text-sm text-muted-foreground">
        30-day trial
      </p>
      <SurfaceCard className="rounded-2xl border-border p-8 shadow-lg">
        {(hasOAuth || !showForm) && (
          <AuthProviderButtons
            oauthProviders={oauthProviders}
            routes={routes}
            emailButton={
              hasOAuth
                ? {
                    label: "Sign up with Email",
                    onClick: () => setShowEmailForm(true),
                  }
                : undefined
            }
          />
        )}

        {showForm && (
          <>
            {hasOAuth && (
              <div className="my-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">or</span>
                <div className="h-px flex-1 bg-border" />
              </div>
            )}
            <form action={routes.signup} method="post">
              <AuthenticityTokenField csrfToken={csrfToken} />
              <div className="mb-4">
                <FieldLabel htmlFor="user_email">Email</FieldLabel>
                <TextInput
                  id="user_email"
                  name="user[email]"
                  type="email"
                  defaultValue={userForm?.email ?? ""}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div className="mb-4">
                <FieldLabel htmlFor="user_password">Password</FieldLabel>
                <TextInput
                  id="user_password"
                  name="user[password]"
                  type="password"
                  placeholder="password"
                  required
                />
              </div>
              <div className="mb-6">
                <FieldLabel htmlFor="user_password_confirmation">
                  Confirm Password
                </FieldLabel>
                <TextInput
                  id="user_password_confirmation"
                  name="user[password_confirmation]"
                  type="password"
                  placeholder="password"
                  required
                />
              </div>
              <div className="mb-6 flex items-start gap-3">
                <input
                  id="user_tos_accepted"
                  type="checkbox"
                  name="user[tos_accepted]"
                  value="1"
                  checked={tosAccepted}
                  onChange={(e) => setTosAccepted(e.target.checked)}
                  required
                  className="mt-1 h-4 w-4 rounded border-input"
                />
                <label
                  htmlFor="user_tos_accepted"
                  className="text-sm text-muted-foreground leading-snug"
                >
                  I agree to the{" "}
                  <AppLink
                    target="_blank"
                    to={routes.terms}
                    variant="premium"
                    className="underline"
                  >
                    Terms of Service
                  </AppLink>{" "}
                  and{" "}
                  <AppLink
                    target="_blank"
                    to={routes.privacy}
                    variant="premium"
                    className="underline"
                  >
                    Privacy Policy
                  </AppLink>
                  .
                </label>
              </div>
              <Button
                type="submit"
                variant="premium"
                className="w-full py-3 font-medium"
                disabled={!tosAccepted}
              >
                Create Account
              </Button>
            </form>
          </>
        )}
      </SurfaceCard>
      <p className="mt-6 text-center text-sm text-muted-foreground">
        Already have an account?{" "}
        <AppLink to={routes.login} variant="premium">
          Log in
        </AppLink>
      </p>
    </AuthPageLayout>
  );
};
